import { useEffect } from 'react';
import { useSession } from '@/session/store';

/** True when the keystroke belongs to something the user is typing into. */
function isEditable(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  if (target.isContentEditable) return true;
  const tag = target.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT';
}

/**
 * Hold space to talk, anywhere on the page.
 *
 * The mic is open only while the key is down, so anything that can swallow the keyup has to stop it
 * too: leaving the window is the one that actually happens, when a permission prompt or an
 * alt-tab takes focus mid-utterance and the release never arrives.
 */
export function usePushToTalk(enabled: boolean): void {
  useEffect(() => {
    if (!enabled) return;

    function onKeyDown(event: KeyboardEvent) {
      if (event.code !== 'Space' || event.repeat || isEditable(event.target)) return;
      event.preventDefault();
      useSession.getState().startMic();
    }

    function onKeyUp(event: KeyboardEvent) {
      if (event.code !== 'Space' || isEditable(event.target)) return;
      event.preventDefault();
      useSession.getState().stopMic();
    }

    function onBlur() {
      useSession.getState().stopMic();
    }

    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('keyup', onKeyUp);
    window.addEventListener('blur', onBlur);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('keyup', onKeyUp);
      window.removeEventListener('blur', onBlur);
    };
  }, [enabled]);
}
